import { useEffect, useState, type ReactNode } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { SessionExpiredDialog } from './SessionExpiredDialog';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

// Persists the last-activity timestamp so a page refresh can't silently
// bypass an already-expired session: `expired` alone lived only in React
// state, so reloading the page (e.g. right after the dialog appeared) reset
// it to `false` and re-rendered the whole app straight through, since the
// underlying Google session was never actually touched — this dialog is a
// soft, client-only inactivity nag, not a real sign-out. Recording activity
// here and checking it again on mount means a reload while already expired
// re-shows the dialog immediately instead of granting a fresh 30 minutes.
const LAST_ACTIVITY_KEY = 'controlTower.lastActivityAt';

function isAlreadyExpired(): boolean {
  const last = Number(window.sessionStorage.getItem(LAST_ACTIVITY_KEY));
  return last > 0 && Date.now() - last >= INACTIVITY_TIMEOUT_MS;
}

function SessionTimeoutWrapper({ children }: { children: ReactNode }) {
  const identityApi = useApi(identityApiRef);
  const [expired, setExpired] = useState(isAlreadyExpired);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      window.sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      clearTimeout(timer);
      timer = setTimeout(() => setExpired(true), INACTIVITY_TIMEOUT_MS);
    };

    // While the dialog is up, activity elsewhere in the page (there isn't
    // any, since it's modal) shouldn't reset the timer — only the dialog's
    // own Cancel button counts as "still here" once expired is true.
    if (expired) {
      return undefined;
    }

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetTimer, true),
    );
    resetTimer();

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, resetTimer, true),
      );
    };
  }, [expired]);

  return (
    <>
      {children}
      <SessionExpiredDialog
        open={expired}
        onContinue={() => {
          // Close our own dialog before signing out — signOut() can itself
          // need a fresh Google token and trigger the OAuth-request dialog
          // (see modules/oauthRequestDialog.tsx); leaving ours open
          // underneath stacked two auth prompts on top of each other
          // instead of a clean handoff.
          window.sessionStorage.removeItem(LAST_ACTIVITY_KEY);
          setExpired(false);
          identityApi.signOut();
        }}
        onCancel={() => setExpired(false)}
      />
    </>
  );
}

export const sessionTimeoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'session-timeout',
      params: {
        component: SessionTimeoutWrapper,
      },
    }),
  ],
});
